import { ChangeDetectionStrategy, Component, OnInit, Signal, computed, input, output, signal } from '@angular/core';
import { map } from 'rxjs';
import { TypeCoreComponent } from 'src/app/pages/type-routes/type-core.component.js';

import { ProgressTypeDb } from 'lib/cosmos/progress-type/progressTypeDb.js';
import { stringHelper } from 'lib/helpers/string-helper.class.js';
import { SelectItemField } from 'lib/ui/form/fields/select-item-field.component.js';
import { FormSaveHandler } from 'lib/ui/form/form-save-button.component.js';

@Component({
	selector: 'mod-progress-type-form',
	templateUrl: './progress-type-form.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class ProgressTypeFormComponent
	extends TypeCoreComponent<ProgressTypeDb.IProgressTypeItem | undefined>
	implements OnInit
{
	public readonly isInDialog = input<boolean>(false);

	public readonly create = output<ProgressTypeDb.IProgressTypeItem>();

	private readonly units: readonly ProgressTypeDb.MetricOnlyProgressTypeUnit[] = [
		'cm',
		'kg',
		'meters',
		'reps',
		'seconds',
		'sets',
		'percent'
	];

	public readonly unitSelectionItems: readonly SelectItemField<ProgressTypeDb.MetricOnlyProgressTypeUnit>[] =
		this.units.map((m) => {
			return {
				data: m,
				title: this.getUnitTitle(m),
				value: m
			};
		});

	public readonly form = {
		id: signal<string | undefined>(undefined),
		name: signal<string | undefined>(undefined),
		description: signal<string | undefined>(undefined),
		unit: signal<ProgressTypeDb.MetricOnlyProgressTypeUnit | undefined>(undefined)
	};

	public readonly isEditForm: Signal<boolean> = computed(() => {
		if (this.form.id()) {
			return true;
		}
		return false;
	});

	public readonly saveHandler = computed<FormSaveHandler>(() => {
		const sharedData = this.sharedData();

		const form = this.form;

		const name = form.name();
		const unit = form.unit();

		if (!name || !unit) {
			return undefined;
		}

		return this.dependencies.httpService
			.upsert<ProgressTypeDb.IProgressTypeItem, ProgressTypeDb.IBaseProgressTypeItem>({
				type: 'progressType',
				existingItem: sharedData?.data,
				createModel: {
					name: name,
					description: form.description(),
					unit: unit
				},
				patchModel: (item) => {
					return {
						id: item.id,
						name: name,
						description: form.description(),
						unit: unit
					};
				}
			})
			.pipe(
				map((response) => {
					super.notifyUpsert('Progress Type', this.isEditForm());
					this.create.emit(response.data);

					super.updateSharedData({
						data: response.data,
						linkedItems: response.linkedItems
					});

					if (!this.isInDialog() && !this.isEditForm()) {
						this.dependencies.navigateService
							.actionRoute((routes) => routes.progressRoutes.progressType.children.edit, response.data.id)
							.navigate();
					}
				})
			);
	});

	constructor() {
		super();

		this.sharedDataInit = (sharedData) => {
			this.form.id.set(sharedData?.data?.id);
			this.form.name.set(sharedData?.data?.system.name);
			this.form.unit.set(sharedData?.data?.unit);
			this.form.description.set(sharedData?.data?.description);
		};
	}

	private getUnitTitle(unit: ProgressTypeDb.MetricOnlyProgressTypeUnit): string {
		let title: string = unit;

		if (unit === 'cm') {
			title = `cm / inches`;
		} else if (unit === 'kg') {
			title = `kg / pounds`;
		} else if (unit === 'meters') {
			title = `meters / feet`;
		}

		return stringHelper.capitalizeText(title);
	}
}
